/**
 * アンケートフォーム管理モジュール
 */

const STORAGE_KEY = 'choiku_enquete';
const SUBMISSION_KEY = 'choiku_enquete_submitted';
const EXPIRY_DAYS = 30;

const getInitialFormData = () => ({
  profile: {
    age: '',
    gender: '',
    contact: '', // 旧 xAccount → contact（連絡先）
  },
  moneyManagement: '', // 設問2: お金の管理
  questions: {
    q3: '', // 旧Q1: 100万円の使い道
    q3Other: '',
    q4: '', // 旧Q3: 推しキャラクター
    q5: [], // 旧Q4: 視聴動画
    q6: [], // 旧Q5: 視聴きっかけ
    q6Other: '',
    q7: '', // 旧Q6: お金の意識変化
    q8: [], // 旧Q7: 実践したいこと
    q8Other: '',
    q9: [], // 旧Q8: Tシャツサイズ
  },
});

const state = {
  formData: getInitialFormData(),
  isSubmitting: false,
  submitError: null,
  isSubmitted: false,
  lastSavedAt: null,
};

const getters = {
  /**
   * フォームデータが空かどうか
   */
  isFormEmpty: (stateData) => {
    const {
      profile, moneyManagement, questions,
    } = stateData.formData;
    return (
      !profile.age
      && !profile.gender
      && !profile.contact
      && !moneyManagement
      && !questions.q3
      && !questions.q4
      && questions.q5.length === 0
      && questions.q6.length === 0
      && !questions.q7
      && questions.q8.length === 0
      && questions.q9.length === 0
    );
  },

  /**
   * 送信可能かどうか
   */
  canSubmit: (stateData) => !stateData.isSubmitting && !stateData.isSubmitted,
};

const mutations = {
  /**
   * フォームデータを更新
   */
  updateFormData(stateData, { section, field, value }) {
    if (stateData.formData[section]) {
      stateData.formData[section][field] = value;
    }
  },

  /**
   * フォームデータ全体を設定
   */
  setFormData(stateData, formData) {
    stateData.formData = formData;
  },

  /**
   * 送信状態を設定
   */
  setSubmitting(stateData, isSubmitting) {
    stateData.isSubmitting = isSubmitting;
  },

  /**
   * 送信エラーを設定
   */
  setSubmitError(stateData, error) {
    stateData.submitError = error;
  },

  /**
   * 送信済み状態を設定
   */
  setSubmitted(stateData, isSubmitted) {
    stateData.isSubmitted = isSubmitted;
  },

  /**
   * 最終保存日時を更新
   */
  setLastSavedAt(stateData, timestamp) {
    stateData.lastSavedAt = timestamp;
  },

  /**
   * フォームをリセット
   */
  resetForm(stateData) {
    stateData.formData = getInitialFormData();
    stateData.isSubmitting = false;
    stateData.submitError = null;
    stateData.lastSavedAt = null;
  },
};

const actions = {
  /**
   * フォームデータを更新し、LocalStorageに保存
   */
  updateField({ commit, dispatch }, payload) {
    commit('updateFormData', payload);
    dispatch('saveToLocalStorage');
  },

  /**
   * LocalStorageにフォームデータを保存
   */
  saveToLocalStorage({ state: stateData, commit }) {
    try {
      const dataToSave = {
        formData: stateData.formData,
        savedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      commit('setLastSavedAt', dataToSave.savedAt);
    } catch (error) {
      console.error('Failed to save form data to localStorage:', error);
    }
  },

  /**
   * LocalStorageからフォームデータを復元
   */
  loadFromLocalStorage({ commit }) {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (!savedData) return false;

      const { formData, savedAt } = JSON.parse(savedData);

      // 有効期限チェック（30日）
      const now = Date.now();
      const expiryTime = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (now - savedAt > expiryTime) {
        // 期限切れの場合はクリア
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      commit('setFormData', formData);
      commit('setLastSavedAt', savedAt);
      return true;
    } catch (error) {
      console.error('Failed to load form data from localStorage:', error);
      return false;
    }
  },

  /**
   * 送信済み状態をチェック
   */
  checkSubmissionStatus({ commit }) {
    try {
      const submitted = localStorage.getItem(SUBMISSION_KEY);
      if (submitted) {
        const submittedData = JSON.parse(submitted);
        const now = Date.now();
        const expiryTime = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

        if (now - submittedData.submittedAt > expiryTime) {
          // 期限切れの場合はクリア
          localStorage.removeItem(SUBMISSION_KEY);
          commit('setSubmitted', false);
          return false;
        }

        commit('setSubmitted', true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to check submission status:', error);
      return false;
    }
  },

  /**
   * 送信完了を記録
   */
  markAsSubmitted({ commit }) {
    try {
      const submissionData = {
        submittedAt: Date.now(),
      };
      localStorage.setItem(SUBMISSION_KEY, JSON.stringify(submissionData));
      commit('setSubmitted', true);

      // フォームデータをクリア
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to mark as submitted:', error);
    }
  },

  /**
   * フォームをクリア（送信済み状態も含む）
   */
  clearAll({ commit }) {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SUBMISSION_KEY);
      commit('resetForm');
      commit('setSubmitted', false);
    } catch (error) {
      console.error('Failed to clear form data:', error);
    }
  },

  /**
   * 送信処理開始
   */
  startSubmission({ commit }) {
    commit('setSubmitting', true);
    commit('setSubmitError', null);
  },

  /**
   * 送信処理完了
   */
  completeSubmission({ commit, dispatch }) {
    commit('setSubmitting', false);
    dispatch('markAsSubmitted');
  },

  /**
   * 送信処理失敗
   */
  failSubmission({ commit }, error) {
    commit('setSubmitting', false);
    commit('setSubmitError', error);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
